<template>
  <!-- Main container for the component -->
  <div
    style="
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 24px;
      width: 100%;
      align-items: start;
    "
  >
    <!-- Car display card component -->
    <CarDisplayCard style="width: 100%" />
    <div class="tw-rounded" style="width: 100%">
      <!-- Title for the trim selection section -->
      <h2 class="tw-text-xl tw-font-medium tw-mb-6">Select Trim</h2>
      <!-- Loading state for custom data -->
      <div v-if="customLoading">
        <!-- Loading animation for the trim selection -->
        <div role="status" class="tw-animate-pulse">
          <div
            class="tw-h-16 tw-bg-gray-200 tw-rounded-lg dark:tw-bg-gray-700 tw-w-full tw-mb-4"
          ></div>
        </div>
        <div role="status" class="tw-animate-pulse">
          <div
            class="tw-h-8 tw-bg-gray-200 tw-rounded-lg dark:tw-bg-gray-700 tw-w-full tw-mb-2"
          ></div>
        </div>
        <div role="status" class="tw-animate-pulse">
          <div
            class="tw-h-8 tw-bg-gray-200 tw-rounded-lg dark:tw-bg-gray-700 tw-w-full tw-mb-2"
          ></div>
        </div>
        <div role="status" class="tw-animate-pulse">
          <div
            class="tw-h-8 tw-bg-gray-200 tw-rounded-lg dark:tw-bg-gray-700 tw-w-full tw-mb-2"
          ></div>
        </div>
      </div>
      <!-- Content displayed when custom data is loaded -->
      <div v-if="!customLoading">
        <!-- Dropdown for selecting drivetrain -->
        <v-select
          label="Select drivetrain"
          :items="drivetrainOptions"
          variant="outlined"
          class="tw-w-full custom-select"
          v-model="selectDriveTrain"
        ></v-select>
        <!-- Radio group for Rear Wheel Drive options -->
        <v-radio-group v-if="selectDriveTrain === 'Rear Wheel Drive'" v-model="selectedTrimId">
          <!-- Loop through RWD options -->
          <div v-for="(item, index) in RWDOptions" :key="item.id">
            <!-- Display body type if it's the first item or different from the previous one -->
            <div
              v-if="index === 0"
              class="tw-font-extralight tw-font-open-sans tw-bg-[#EEEEEE] tw-px-2 tw-py-1 tw-my-2"
            >
              {{ item.bodyType }}
            </div>
            <div
              v-else-if="item.bodyType !== RWDOptions[index - 1].bodyType"
              class="tw-font-extralight tw-font-open-sans tw-bg-[#EEEEEE] tw-px-2 tw-py-1 tw-my-2"
            >
              {{ item.bodyType }}
            </div>
            <div class="tw-flex tw-w-full tw-justify-between tw-items-center mt-1">
              <v-radio
                density="compact"
                :ripple="false"
                color="primary"
                :label="item.name"
                class="custom-radio tw-text-sm tw-font-open-sans tw-text-black"
                :value="item.id"
              ></v-radio>
              <p class="tw-text-sm tw-font-extralight tw-font-open-sans tw-text-black">
                ${{ item.baseMsrp.toLocaleString() }}
              </p>
            </div>
          </div>
        </v-radio-group>
        <!-- Radio group for Four Wheel Drive options -->
        <v-radio-group v-if="selectDriveTrain === 'Four Wheel Drive'" v-model="selectedTrimId">
          <!-- Loop through FourWD options -->
          <div v-for="(item, index) in FourWDOptions" :key="item.id">
            <!-- Display body type if it's the first item or different from the previous one -->
            <div
              v-if="index === 0"
              class="tw-font-extralight tw-font-open-sans tw-bg-[#EEEEEE] tw-px-2 tw-py-1 tw-my-2"
            >
              {{ item.bodyType }}
            </div>
            <div
              v-else-if="item.bodyType !== FourWDOptions[index - 1].bodyType"
              class="tw-font-extralight tw-font-open-sans tw-bg-[#EEEEEE] tw-px-2 tw-py-1 tw-my-2"
            >
              {{ item.bodyType }}
            </div>
            <div class="tw-flex tw-w-full tw-justify-between tw-items-center mt-1">
              <v-radio
                density="compact"
                :ripple="false"
                color="primary"
                :label="item.name"
                class="custom-radio tw-text-sm tw-font-open-sans tw-text-black"
                :value="item.id"
              ></v-radio>
              <p class="tw-text-sm tw-font-extralight tw-font-open-sans tw-text-black">
                ${{ item.baseMsrp.toLocaleString() }}
              </p>
            </div>
          </div>
        </v-radio-group>
        <!-- Radio group for Front Wheel Drive options -->
        <v-radio-group v-if="selectDriveTrain === 'Front Wheel Drive'" v-model="selectedTrimId">
          <!-- Loop through FWD options -->
          <div v-for="(item, index) in FWDOptions" :key="item.id">
            <!-- Display body type if it's the first item or different from the previous one -->
            <div
              v-if="index === 0"
              class="tw-font-extralight tw-font-open-sans tw-bg-[#EEEEEE] tw-px-2 tw-py-1 tw-my-2"
            >
              {{ item.bodyType }}
            </div>
            <div
              v-else-if="item.bodyType !== FWDOptions[index - 1].bodyType"
              class="tw-font-extralight tw-font-open-sans tw-bg-[#EEEEEE] tw-px-2 tw-py-1 tw-my-2"
            >
              {{ item.bodyType }}
            </div>
            <div class="tw-flex tw-w-full tw-justify-between tw-items-center mt-1">
              <v-radio
                density="compact"
                :ripple="false"
                color="primary"
                :label="item.name"
                class="custom-radio tw-text-sm tw-font-open-sans tw-text-black"
                :value="item.id"
              ></v-radio>
              <p class="tw-text-sm tw-font-extralight tw-font-open-sans tw-text-black">
                ${{ item.baseMsrp.toLocaleString() }}
              </p>
            </div>
          </div>
        </v-radio-group>
        <!-- Radio group for All Wheel Drive options -->
        <v-radio-group v-if="selectDriveTrain === 'All Wheel Drive'" v-model="selectedTrimId">
          <!-- Loop through AWD options -->
          <div v-for="(item, index) in AWDOptions" :key="item.id">
            <!-- Display body type if it's the first item or different from the previous one -->
            <div
              v-if="index === 0"
              class="tw-font-extralight tw-font-open-sans tw-bg-[#EEEEEE] tw-px-2 tw-py-1 tw-my-2"
            >
              {{ item.bodyType }}
            </div>
            <div
              v-else-if="item.bodyType !== AWDOptions[index - 1].bodyType"
              class="tw-font-extralight tw-font-open-sans tw-bg-[#EEEEEE] tw-px-2 tw-py-1 tw-my-2"
            >
              {{ item.bodyType }}
            </div>
            <div class="tw-flex tw-w-full tw-justify-between tw-items-center mt-1">
              <v-radio
                density="compact"
                :ripple="false"
                color="primary"
                :label="item.name"
                class="custom-radio tw-text-sm tw-font-open-sans tw-text-black"
                :value="item.id"
              ></v-radio>
              <p class="tw-text-sm tw-font-extralight tw-font-open-sans tw-text-black">
                ${{ item.baseMsrp.toLocaleString() }}
              </p>
            </div>
          </div>
        </v-radio-group>
      </div>
      <!-- Buttons for comparing trims and proceeding to the next step -->
      <div class="tw-flex tw-justify-end tw-mt-10 tw-gap-2">
        <CompareDialog
          :AllWDOptions="AllWDOptions"
          @toggle-dialog="toggleComapreDialog"
          v-model="compareDialog"
        />
        <v-btn
          class="tw-h-9 tw-w-24 !tw-bg-[#2E66FF] !tw-text-white hover:!tw-bg-[#F5D101]"
          variant="tonal"
          @click="getDataUpdateStep"
        >
          Next
        </v-btn>
      </div>
    </div>
  </div>
  <!-- Table for displaying selected trims comparison -->
  <div
    v-if="selectedForCompare.length > 0"
    class="tw-w-full tw-rounded tw-border tw-border-[#cccccc] tw-shadow tw-mt-12"
  >
    <div class="tw-w-full">
      <div class="tw-flow-root">
        <div class="tw-overflow-x-auto">
          <table class="tw-min-w-full tw-divide-y tw-divide-gray-300 tw-border tw-border-gray-300">
            <thead>
              <tr>
                <th
                  class="tw-border tw-border-gray-300 tw-py-3.5 tw-pl-4 tw-pr-3 tw-text-left tw-text-sm tw-font-roboto !tw-font-medium"
                >
                  <div class="tw-font-roboto">
                    <div class="tw-text-lg">Trims Comparison</div>
                    <div @click="toggleComapreDialog" class="tw-text-sm tw-cursor-pointer">
                      SELECT TRIMS
                      <v-icon
                        size="16px"
                        class="tw-ml-[2px] tw-mb-1"
                        icon="fa fa-plus-circle"
                      ></v-icon>
                    </div>
                  </div>
                </th>
                <!-- Loop through selected trims for comparison -->
                <th
                  v-for="variant in selectedForCompare"
                  :key="variant.item.id"
                  class="tw-border tw-border-gray-300 tw-py-3.5 tw-pl-4 tw-pr-3 tw-text-left tw-text-sm tw-font-roboto !tw-font-medium"
                >
                  {{ variant.item?.style?.styleName }}
                </th>
              </tr>
            </thead>
            <tbody class="tw-divide-y tw-divide-gray-200">
              <!-- Loop through comparison data groups -->
              <template v-for="group in comparisonData" :key="group.groupName">
                <tr class="tw-bg-[#DBDBDB] tw-h-9 tw-font-roboto tw-font-bold tw-text-sm">
                  <th
                    :colspan="selectedForCompare.length < 3 ? 4 : selectedForCompare.length + 1"
                    class="tw-border tw-border-gray-300 tw-py-2 tw-pl-4 tw-pr-3 tw-text-left tw-text-gray-900"
                  >
                    {{ group.groupName }}
                  </th>
                </tr>
                <!-- Loop through features within each group -->
                <tr
                  v-for="(item, index) in group.features"
                  :key="item.featureDescription"
                  class="hover:tw-bg-[#E9E9E9]"
                  :class="index % 2 != 0 ? 'tw-bg-[#eeeeee]' : 'tw-bg-white'"
                >
                  <td
                    class="tw-border tw-border-gray-300 tw-whitespace-nowrap tw-py-4 tw-pl-4 tw-pr-3 tw-text-sm tw-text-gray-900"
                  >
                    {{ item.featureDescription }}
                  </td>
                  <!-- Loop through selected values for each feature -->
                  <td
                    v-for="(value, index) in item.selectedValues"
                    :key="index"
                    :class="`tw-border tw-border-gray-300 tw-whitespace-nowrap tw-py-4 tw-pl-4 tw-pr-3 tw-text-sm tw-text-gray-900`"
                  >
                    {{ value }}
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import CompareDialog from '../common/Dialog/CompareDialog.vue';
import { useCarsStore } from '@/stores/useCarsStore';
import { storeToRefs } from 'pinia';
import CarDisplayCard from '@/components/common/cards/CarDisplayCard.vue';
import { useVariablesStore } from '@/stores/useVariablesStore';
import { useSelectionQuery } from '@/composables/useSelectionQuery';

const emit = defineEmits(['nextStep']);
const variablesStore = useVariablesStore();
const { selectedForCompare } = storeToRefs(variablesStore);
const carStore = useCarsStore();
const { filteredCarDetails } = storeToRefs(carStore);
const { getConfigurationDetails, getStyleImages } = carStore;
const { syncQueryToUrl } = useSelectionQuery();
const {
  selectedModalDetails,
  modelVariantId,
  modelsCompareDetails,
  modelConfigurationDetails,
} = storeToRefs(carStore);

const compareDialog = ref(false);
const customLoading = ref(false);
const selectDrive = ref(null);
const allowAutoSelectStyle = ref(false);
const isInitializingTrim = ref(false);
const drivetrainOptions = computed(() => [
  ...new Set((selectedModalDetails.value || []).map((item) => item.drivetrain)),
]);
const selectDriveTrain = ref(drivetrainOptions.value[0] || null);
const selectedTrimId = computed({
  get() {
    return selectDrive.value?.id ?? modelVariantId.value ?? null;
  },
  set(nextId) {
    const styles = selectedModalDetails.value || [];
    const matched =
      styles.find((item) => Number(item.id) === Number(nextId)) || null;
    if (matched) {
      allowAutoSelectStyle.value = true;
      selectDriveTrain.value = matched.drivetrain;
      selectDrive.value = matched;
    }
  },
});
const RWDOptions = computed(() => {
  return (selectedModalDetails.value || []).filter(
    (modal) => modal.drivetrain === 'Rear Wheel Drive',
  );
});
const FWDOptions = computed(() => {
  return (selectedModalDetails.value || []).filter(
    (modal) => modal.drivetrain === 'Front Wheel Drive',
  );
});
const FourWDOptions = computed(() => {
  return (selectedModalDetails.value || []).filter(
    (modal) => modal.drivetrain === 'Four Wheel Drive',
  );
});
const AWDOptions = computed(() => {
  return (selectedModalDetails.value || []).filter(
    (modal) => modal.drivetrain === 'All Wheel Drive',
  );
});

const AllWDOptions = computed(() => {
  return [...FWDOptions.value, ...FourWDOptions.value, ...AWDOptions.value, ...RWDOptions.value];
});

const optionsMap = computed(() => ({
  'Rear Wheel Drive': RWDOptions.value,
  'Front Wheel Drive': FWDOptions.value,
  'Four Wheel Drive': FourWDOptions.value,
  'All Wheel Drive': AWDOptions.value,
}));

function syncTrimSelectionFromStore() {
  const styles = selectedModalDetails.value || [];
  if (!styles.length) {
    selectDrive.value = null;
    selectDriveTrain.value = null;
    return;
  }

  const matchedStyle =
    modelVariantId.value != null
      ? styles.find((item) => Number(item.id) === Number(modelVariantId.value))
      : null;

  if (matchedStyle) {
    selectDriveTrain.value = matchedStyle.drivetrain;
    selectDrive.value = matchedStyle;
    return;
  }

  if (!selectDriveTrain.value || !drivetrainOptions.value.includes(selectDriveTrain.value)) {
    selectDriveTrain.value = drivetrainOptions.value[0] || null;
  }
  selectDrive.value = null;
}

async function initDefaultValues() {
  const styles = selectedModalDetails.value || [];
  if (!styles.length) {
    selectDrive.value = null;
    selectDriveTrain.value = null;
    return;
  }

  let style =
    modelVariantId.value != null
      ? styles.find((item) => Number(item.id) === Number(modelVariantId.value))
      : null;

  if (!style) {
    const driveTrain = drivetrainOptions.value[0] || null;
    style = (driveTrain && optionsMap.value[driveTrain]?.[0]) || styles[0] || null;
  }

  if (!style?.id) return;

  isInitializingTrim.value = true;
  try {
    allowAutoSelectStyle.value = true;
    selectDriveTrain.value = style.drivetrain;
    selectDrive.value = style;

    carStore.$patch({
      modelVariantId: style.id,
      selectedColor: null,
    });

    syncQueryToUrl({ includeColor: false });

    await getConfigurationDetails();
    await getStyleImages({ commitSelection: false });
  } finally {
    isInitializingTrim.value = false;
  }
}

const processComparisonValues = (data, forCompare) => {
  if ((data, forCompare)) {
    return data.map((group) => ({
      groupName: group.groupName,
      features: group.comparisonItems.map((item) => ({
        featureDescription: item.featureDescription,
        selectedValues: forCompare.map((selected) => item.comparisonValues[selected.index]),
      })),
    }));
  }
  return [];
};

const comparisonData = ref([]);

watch(selectedForCompare, () => {
  if (
    modelsCompareDetails.value?.comparisonGroups &&
    Object.keys(modelsCompareDetails.value.comparisonGroups).length > 0
  ) {
    comparisonData.value = processComparisonValues(
      modelsCompareDetails.value.comparisonGroups,
      selectedForCompare.value,
    );
  }
});

watch([selectedModalDetails, modelVariantId], () => {
  allowAutoSelectStyle.value = false;
  syncTrimSelectionFromStore();
}, { immediate: true });

watch(selectDriveTrain, (nextDriveTrain, prevDriveTrain) => {
  if (!nextDriveTrain) return;
  if (selectDrive.value?.drivetrain === nextDriveTrain) return;

  const styles = selectedModalDetails.value || [];
  const storeMatch =
    modelVariantId.value != null
      ? styles.find((item) => Number(item.id) === Number(modelVariantId.value))
      : null;
  if (storeMatch?.drivetrain === nextDriveTrain) {
    selectDrive.value = storeMatch;
    return;
  }

  if (prevDriveTrain == null && !allowAutoSelectStyle.value) {
    return;
  }

  const currentOptions = optionsMap.value[nextDriveTrain] || [];
  if (currentOptions.length > 0) {
    allowAutoSelectStyle.value = true;
    selectDrive.value = currentOptions[0];
  }
});

watch(selectDrive, () => {
  if (isInitializingTrim.value) return;
  if (!selectDrive.value?.id) return;
  const nextVariantId = selectDrive.value.id;
  const trimChanged = modelVariantId.value !== nextVariantId;

  if (!trimChanged && carStore.modelConfigurationDetails) {
    return;
  }

  if (!allowAutoSelectStyle.value && Number(modelVariantId.value) !== Number(nextVariantId)) {
    return;
  }

  carStore.$patch({
    modelVariantId: nextVariantId,
    selectedColor: null,
  });
  syncQueryToUrl({ includeColor: false });
  getConfigurationDetails().then(() => {
    getStyleImages({ commitSelection: false });
  });
});

const toggleComapreDialog = () => {
  compareDialog.value = !compareDialog.value;
};

const getDataUpdateStep = async () => {
  if (!modelConfigurationDetails.value?.options?.length && modelVariantId.value) {
    await getConfigurationDetails();
    await getStyleImages({ commitSelection: false });
  }
  if (modelConfigurationDetails.value?.options) {
    filteredCarDetails.value = modelConfigurationDetails.value.options.filter(
      (filteredOption) => filteredOption.optionKindName != 'Primary Exterior Color',
    );
    const primaryInteriorColorIndex = filteredCarDetails.value.findIndex(
      (item) => item.optionKindName == 'Seat Color',
    );
    if (primaryInteriorColorIndex >= 0) {
      const [primaryInteriorColor] = filteredCarDetails.value.splice(primaryInteriorColorIndex, 1);
      filteredCarDetails.value.splice(0, 0, primaryInteriorColor);
    }
  }
  syncQueryToUrl();
  emit('nextStep');
};

watch(modelsCompareDetails, (newVal) => {
  if (newVal) {
    customLoading.value = false;
  } else {
    customLoading.value = true;
  }
}, { immediate: true });

onMounted(() => {
  initDefaultValues();
});
</script>

<style>
/* Custom styles for radio buttons */
.custom-radio .v-icon {
  @apply tw-text-sm tw-mt-[2px];
}

/* Custom styles for select field */
.custom-select .v-field--focused .v-field-label {
  @apply tw-text-[#b58af3];
}
</style>
